import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import {
  Save, ArrowLeft, Paperclip, History, Calendar, ShieldCheck,
  ChevronLeft, ChevronRight, Layout, Settings, Layers, Trash2,
  Workflow, Plus, Minus, Clock, Zap, Search, AlertTriangle, List, X
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import WorkflowActionModal from './WorkflowActionModal';
import SmartPrintButton from './PrintButton';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import AuditHistoryModal from './AuditHistoryModal'
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

const SLADetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = !id || id === 'new';

    const { fontSize, changeFontSize } = useFontSize();
    const { canCreate, canUpdate, canDelete } = usePermissions('sla');
    const [activeTab, setActiveTab] = useState('Agreement');
    const [loading, setLoading] = useState(false);

    // --- Modals State ---
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
    const [showAudit, setShowAudit] = useState(false);
    const [attCount, setAttCount] = useState(0);

    const [nav, setNav] = useState({ next: null, prev: null, current_pos: 0, total: 0 });

    const session = JSON.parse(localStorage.getItem('user') || '{}');

    // --- Master Data State ---
    const [data, setData] = useState({
        slanum: '', description: '', sla_type: 'VENDOR', status: 'DRAFT',
        ranking: 1, applies_to: 'WORKORDER', contract_num: '',
        vendor_id: '', start_date: '', end_date: '',
        org_id: session.org_id, site_id: session.site_id,
        commitments: [],
        escalations: [],
        assets_locations: []
    });

    const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '' });
    const isReadOnly = ['ACTIVE', 'EXPIRED', 'APPROVED'].includes(data.status);

    // 🎯 1. جلب البيانات الشاملة (API Sync)
    const loadSLA = useCallback(async () => {
        if (isNew) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const res = await axios.get(`/api/sla/${id}`, { headers });
            setData(res.data);

            const navRes = await axios.get(`/api/sla/${id}/navigation${location.search}`, { headers });
            setNav(navRes.data);

            const attRes = await axios.get(`/api/attachments/count/SLA/${id}`, { headers });
            setAttCount(attRes.data.count);
        } catch (err) {
            toast.error("Error Synchronizing SLA Node");
        } finally { setLoading(false); }
    }, [id, isNew, location.search]);

    useEffect(() => { loadSLA(); }, [loadSLA]);

    // 🎯 2. دالة الحفظ الشاملة
    const handleSave = async () => {
        if (!data.slanum || !data.description) return toast.error("SLA Number and Description are mandatory");
        const tid = toast.loading("Executing SLA Master Save...");
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/sla/save', data, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success("SLA Logic Synchronized", { id: tid });
            if (isNew) navigate(`/sla/${res.data.slanum}${location.search}`);
            else loadSLA();
        } catch (err) {
            toast.error("Save Operation Aborted");
            toast.dismiss(tid);
        }
    };

    // 🎯 3. دالة الحذف
    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Purge SLA Record?', text: 'This will erase all commitments and escalation triggers permanently.',
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'CONFIRM ERASE'
        });
        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`/api/sla/${data.sla_id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                toast.success("SLA Data Purged");
                navigate('/sla');
            } catch (err) { toast.error("Deletion Rejected by Server"); }
        }
    };

    // 🎯 4. الـ Global Listeners (Sidebar Integration)
    useEffect(() => {
        const saveH = () => handleSave();
        const statusH = () => setShowStatusModal(true);
        const statusHistory = () => setShowHistoryModal(true);
        const openLogModal = () => setShowAudit(true);
        const deleteH = () => handleDelete();
        window.addEventListener('GLOBAL_SAVE_HANDLER', saveH);
        window.addEventListener('GLOBAL_STATUS_HANDLER', statusH);
        window.addEventListener('OPEN_STATUS_HISTORY', statusHistory);
        window.addEventListener('GLOBAL_LOG_HANDLER', openLogModal);
        window.addEventListener('GLOBAL_DELETE_HANDLER', deleteH);

        return () => {
            window.removeEventListener('GLOBAL_SAVE_HANDLER', saveH);
            window.removeEventListener('GLOBAL_STATUS_HANDLER', statusH);
            window.removeEventListener('OPEN_STATUS_HISTORY', statusHistory);
            window.removeEventListener('GLOBAL_LOG_HANDLER', openLogModal);
            window.removeEventListener('GLOBAL_DELETE_HANDLER', deleteH);
        };
    }, [data]);


    const goTo = (target) => target && navigate(`/sla/${target}${location.search}`);

    // --- Action Handlers ---
    const addEscalation = () => {
        setData({
            ...data,
            escalations: [...data.escalations, { description: '', condition: '', actions: [] }]
        });
    };

    const addAction = (escIndex) => {
        const newEsc = [...data.escalations];
        newEsc[escIndex].actions = [
            ...(newEsc[escIndex].actions || []),
            { action_type: 'NOTIFICATION', value: '', recipient_role: '' }
        ];
        setData({ ...data, escalations: newEsc });
    };

    return (
        <div className="flex flex-col h-full bg-surface-app font-black uppercase tracking-tighter" style={{ fontSize: `${fontSize}px` }}>
            <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} onSelect={row => {
                // Logic for bulk asset/loc or single fields
                if (lookup.field === 'bulk_asset') {
                    setData({...data, assets_locations: [...data.assets_locations, { type: 'ASSET', ref_id: row.assetnum, description: row.description }]});
                } else if (lookup.field === 'bulk_loc') {
                    setData({...data, assets_locations: [...data.assets_locations, { type: 'LOCATION', ref_id: row.location_id, description: row.description }]});
                } else if (lookup.field.startsWith('esc_')) {
                    // Logic to handle role lookup for specific escalation action
                    const [_, escIdx, __, actIdx] = lookup.field.split('_');
                    const newEsc = [...data.escalations];
                    newEsc[escIdx].actions[actIdx].recipient_role = row.role_id || row.value;
                    setData({...data, escalations: newEsc});
                } else {
                    setData({...data, [lookup.field]: row.id || row.value});
                }
                setLookup({...lookup, isOpen: false});
            }} />

            {/* --- TOOLBAR --- */}
            <div className="bg-primary-dark text-white p-2 flex justify-between items-center shadow-lg px-6 shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(`/sla${location.search}`)} className="p-2 hover:bg-white/10 rounded-full transition-all active:scale-90"><ArrowLeft size={18}/></button>
                    {!isNew && (
                        <div className="flex items-center bg-black/30 border border-white/10 rounded px-2 py-0.5 gap-2 font-mono text-[10px]">
                            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="hover:text-status-warning disabled:opacity-20"><ChevronLeft size={18}/></button>
                            <span className="text-status-warning tracking-widest px-2">{nav.current_pos} / {nav.total}</span>
                            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="hover:text-status-warning disabled:opacity-20"><ChevronRight size={18}/></button>
                        </div>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    {((isNew && canCreate) || (!isNew && canUpdate)) && (
                    <button onClick={handleSave} disabled={isReadOnly} className="bg-status-success px-8 py-2.5 rounded-sm flex items-center gap-2 hover:bg-status-success shadow-md transition-all active:scale-95 disabled:opacity-30 font-black">
                        <Save size={16}/> SAVE SLA MASTER
                    </button>
                    )}
                    <div className="h-6 w-px bg-white/20 mx-2"></div>
                    <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-white/70 hover:text-white transition-colors">
                        <Paperclip size={20}/>
                        {attCount > 0 && <span className="absolute -top-1 -right-1 bg-status-danger w-4 h-4 rounded-full text-[8px] flex items-center justify-center border border-white font-black">{attCount}</span>}
                    </button>
                    <WorkflowActionModal moduleName="SLA" refId={id} currentStatus={data.status} onActionComplete={loadSLA} />
                    {!isNew && <SmartPrintButton moduleName="SLA" refId={data.slanum} />}
                    <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 text-status-warning" title="Workflow History"><Workflow size={20}/></button>
                    <button onClick={() => setShowHistoryModal(true)} className="p-2 text-white/70" title="Status History"><History size={20}/></button>
                    {canDelete && !isNew && <button onClick={handleDelete} className="p-2 text-status-danger hover:bg-status-danger/20 rounded"><Trash2 size={20}/></button>}

                    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/10 ml-4">
                        <button onClick={() => changeFontSize(-1)} className="hover:text-status-warning"><Minus size={14} /></button>
                        <span className="font-black text-[10px] text-white/70">{fontSize}</span>
                        <button onClick={() => changeFontSize(1)} className="hover:text-status-warning"><Plus size={14} /></button>
                    </div>
                </div>
            </div>

            {/* --- HEADER --- */}
            <div className="bg-surface-card border-b px-8 py-5 flex items-center gap-12 shadow-sm shrink-0">
                <HeaderField label="SLA Agreement #" value={data.slanum} isBold width="w-48" disabled={!isNew} onChange={v => setData({...data, slanum: v})} placeholder="SLA-XXXX" />
                <HeaderField label="Description" value={data.description} width="w-[600px]" onChange={v => setData({...data, description: v})} disabled={isReadOnly} />
                <HeaderField label="Ranking" value={data.ranking} type="number" width="w-24" onChange={v => setData({...data, ranking: v})} disabled={isReadOnly} />
                <HeaderField label="SLA Status" value={data.status} isStatus onClick={() => setShowStatusModal(true)} width="w-32" />
            </div>

            {/* --- TABS --- */}
            <div className="flex bg-surface-card border-b px-4 shrink-0 shadow-sm z-10">
                {['Agreement', 'Commitments', 'Assets & Locations', 'Escalations'].map(t => (
                    <button key={t} onClick={() => setActiveTab(t)} className={`px-12 py-4 text-[10px] border-b-4 transition-all font-black ${activeTab === t ? 'border-primary-dark text-primary-dark bg-surface-panel shadow-inner' : 'border-transparent text-ink-secondary hover:text-ink'}`}>{t}</button>
                ))}
            </div>

            {/* --- CONTENT --- */}
            <div className="flex-1 overflow-y-auto p-8 bg-surface-panel custom-scrollbar">
                <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-top-4 duration-700 pb-20">

                    {/* Tab: Agreement */}
                    {activeTab === 'Agreement' && (
                        <div className="grid grid-cols-12 gap-10">
                            <div className="col-span-8">
                                <SectionCard title="SLA Agreement Framework">
                                    <div className="grid grid-cols-2 gap-x-20 gap-y-10">
                                        <div className="flex flex-col border-b py-2">
                                            <label className="text-[9px] text-ink-secondary italic mb-1 uppercase font-black">SLA Type</label>
                                            <select className="bg-transparent outline-none font-black text-primary text-[14px] uppercase" value={data.sla_type} onChange={e => setData({...data, sla_type: e.target.value})} disabled={isReadOnly}>
                                                <option value="VENDOR">VENDOR SLA (Incoming)</option>
                                                <option value="CUSTOMER">CUSTOMER SLA (Outgoing)</option>
                                                <option value="INTERNAL">INTERNAL SLA (Opr)</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col border-b py-2">
                                            <label className="text-[9px] text-ink-secondary italic mb-1 uppercase font-black">Applies To</label>
                                            <select className="bg-transparent outline-none font-black text-status-warning text-[14px] uppercase" value={data.applies_to} onChange={e => setData({...data, applies_to: e.target.value})} disabled={isReadOnly}>
                                                <option value="WORKORDER">WORK ORDERS (CM/PM)</option>
                                                <option value="PO">PURCHASE ORDERS</option>
                                                <option value="TICKET">SERVICE TICKETS</option>
                                            </select>
                                        </div>
                                        <DataField label="Linked Contract" value={data.contract_num} onSearch={() => setLookup({isOpen: true, title: 'Lookup Contracts', type: 'contracts', field: 'contract_num'})} disabled={isReadOnly} />
                                        <DataField label="Vendor Reference" value={data.vendor_id} onSearch={() => setLookup({isOpen: true, title: 'Lookup Vendors', type: 'companies', field: 'vendor_id'})} disabled={isReadOnly} />
                                    </div>
                                </SectionCard>
                            </div>
                            <div className="col-span-4">
                                <SectionCard title="Lifecycle Control">
                                    <div className="space-y-6">
                                        <DataField label="Effective From" type="date" value={data.start_date} onChange={v => setData({...data, start_date: v})} disabled={isReadOnly} />
                                        <DataField label="Expires On" type="date" value={data.end_date} onChange={v => setData({...data, end_date: v})} disabled={isReadOnly} />
                                        <StaticField label="Site Node" value={data.site_id} icon={<ShieldCheck size={14}/>} />
                                        <StaticField label="Organization" value={data.org_id} icon={<Layout size={14}/>} />
                                    </div>
                                </SectionCard>
                            </div>
                        </div>
                    )}

                    {/* Tab: Commitments */}
                    {activeTab === 'Commitments' && (
                        <div className="bg-surface-card border-2 border-line-subtle rounded shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-500">
                            <div className="p-4 bg-surface-panel border-b flex justify-between items-center">
                                <h3 className="font-black text-primary-dark text-[11px] flex items-center gap-3 tracking-widest italic uppercase"><Clock size={20} className="text-status-warning"/> Service Level Commitments</h3>
                                <button disabled={isReadOnly} onClick={() => setData({...data, commitments: [...data.commitments, {description: '', type: 'RESPONSE', value: 0, unit_of_measure: 'HRS', penalty_rate: 0.0}]})} className="bg-primary-dark text-white px-8 py-2 rounded-sm font-black text-[10px] flex items-center gap-2 hover:bg-black transition-all shadow-md uppercase">
                                    <Plus size={16}/> APPEND COMMITMENT
                                </button>
                            </div>
                            <table className="w-full text-left">
                                <thead className="bg-surface-panel border-b text-ink-secondary text-[9px] font-black uppercase tracking-widest">
                                    <tr>
                                        <th className="p-5 border-r w-12 text-center">#</th>
                                        <th className="p-5 border-r">Commitment Description</th>
                                        <th className="p-5 border-r text-center w-48">Metric Type</th>
                                        <th className="p-5 border-r text-center w-40">Target Value</th>
                                        <th className="p-5 border-r text-center w-40">Unit</th>
                                        <th className="p-5 border-r text-center w-40">Penalty Rate (Per Hr Over)</th>
                                        <th className="p-5 w-16"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle">
                                    {data.commitments.map((c, i) => (
                                        <tr key={i} className="hover:bg-primary-soft/50 transition-colors font-bold">
                                            <td className="p-5 border-r text-center text-ink-secondary font-mono italic">{i+1}</td>
                                            <td className="p-5 border-r">
                                                <input className="w-full bg-transparent outline-none font-black text-primary" placeholder="Description..." value={c.description} onChange={e => { const nc = [...data.commitments]; nc[i].description = e.target.value; setData({...data, commitments: nc}) }} disabled={isReadOnly} />
                                            </td>
                                            <td className="p-5 border-r text-center">
                                                <select className="bg-transparent font-black text-primary outline-none uppercase" value={c.type} onChange={e => { const nc = [...data.commitments]; nc[i].type = e.target.value; setData({...data, commitments: nc}) }} disabled={isReadOnly}>
                                                    <option value="RESPONSE">RESPONSE TIME</option>
                                                    <option value="RESOLUTION">RESOLUTION TIME</option>
                                                    <option value="DELIVERY">DELIVERY LEAD</option>
                                                    <option value="UPTIME">UPTIME %</option>
                                                </select>
                                            </td>
                                            <td className="p-5 border-r text-center">
                                                <input type="number" className="w-full text-center bg-transparent outline-none font-black text-status-warning" value={c.value} onChange={e => { const nc = [...data.commitments]; nc[i].value = e.target.value; setData({...data, commitments: nc}) }} disabled={isReadOnly} />
                                            </td>
                                            <td className="p-5 border-r text-center">
                                                <select className="bg-transparent outline-none uppercase font-black" value={c.unit_of_measure} onChange={e => { const nc = [...data.commitments]; nc[i].unit_of_measure = e.target.value; setData({...data, commitments: nc}) }} disabled={isReadOnly}>
                                                    <option value="MINS">MINS</option>
                                                    <option value="HRS">HRS</option>
                                                    <option value="DAYS">DAYS</option>
                                                    <option value="PERCENT">%</option>
                                                </select>
                                            </td>
                                            <td className="p-5 border-r text-center">
                                                <input type="number" className="w-full text-center bg-transparent outline-none font-black text-status-warning" value={c.penalty_rate} onChange={e => { const nc = [...data.commitments]; nc[i].penalty_rate = e.target.penalty_rate; setData({...data, commitments: nc}) }} disabled={isReadOnly} />
                                                <div className="w-full text-center bg-transparent text-[0.8em] text-status-warning font-bold italic animate-in fade-in slide-in-from-left-1 leading-none pb-1">EGP</div>
                                            </td>
                                            <td className="p-5 text-center">
                                                <button disabled={isReadOnly} onClick={() => { const nc = data.commitments.filter((_, idx) => idx !== i); setData({...data, commitments: nc}) }}><Trash2 size={18} className="text-ink-secondary hover:text-status-danger transition-all"/></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Tab: Assets & Locations */}
                    {activeTab === 'Assets & Locations' && (
                         <div className="bg-surface-card border-2 border-line-subtle rounded shadow-2xl overflow-hidden">
                            <div className="p-4 bg-surface-panel border-b flex justify-between items-center">
                                <h3 className="font-black text-primary-dark text-[11px] flex items-center gap-3 tracking-widest italic uppercase"><Layers size={20} className="text-primary"/> Applicability Scope</h3>
                                <div className="flex gap-2">
                                    <button disabled={isReadOnly} onClick={() => setLookup({isOpen: true, title: 'Select Assets', type: 'assets', field: 'bulk_asset'})} className="bg-surface-card border-2 border-primary text-primary px-6 py-2 rounded-sm font-black text-[10px] hover:bg-primary-soft transition-all">LINK ASSETS</button>
                                    <button disabled={isReadOnly} onClick={() => setLookup({isOpen: true, title: 'Select Locations', type: 'locations', field: 'bulk_loc'})} className="bg-primary-dark text-white px-6 py-2 rounded-sm font-black text-[10px] hover:bg-black transition-all">LINK LOCATIONS</button>
                                </div>
                            </div>
                            <table className="w-full text-left font-black">
                                <thead className="bg-surface-panel border-b text-ink-secondary text-[9px] uppercase">
                                    <tr>
                                        <th className="p-5 border-r w-48">Scope Type</th>
                                        <th className="p-5 border-r w-64">Reference ID</th>
                                        <th className="p-5">Description</th>
                                        <th className="p-5 w-16"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle">
                                    {data.assets_locations.map((item, i) => (
                                        <tr key={i} className="hover:bg-surface-panel italic">
                                            <td className="p-5 border-r text-primary">{item.type}</td>
                                            <td className="p-5 border-r font-mono tracking-widest">{item.ref_id}</td>
                                            <td className="p-5 text-ink-secondary text-[12px]">{item.description}</td>
                                            <td className="p-5 text-center"><button onClick={() => setData({...data, assets_locations: data.assets_locations.filter((_, idx) => idx !== i)})}><Trash2 size={16} className="text-ink-secondary hover:text-status-danger"/></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                         </div>
                    )}

                    {/* Tab: Escalations (The Masterpiece) */}
                    {activeTab === 'Escalations' && (
                        <div className="bg-surface-card border-2 border-line-subtle rounded shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500">
                             <div className="p-4 bg-surface-panel border-b flex justify-between items-center">
                                <h3 className="font-black text-primary-dark text-[11px] flex items-center gap-3 tracking-widest italic uppercase"><Zap size={20} className="text-yellow-500"/> Escalation Points & Notification Logic</h3>
                                <button disabled={isReadOnly} onClick={addEscalation} className="bg-primary-dark text-white px-8 py-2 rounded-sm font-black text-[10px] flex items-center gap-2 hover:bg-black shadow-md uppercase">+ ADD ESCALATION POINT</button>
                            </div>
                            <div className="p-8 space-y-12">
                                {data.escalations.map((esc, i) => (
                                    <div key={i} className="bg-surface-card border-2 border-line p-8 rounded shadow-sm relative group border-l-8 border-l-status-warning">
                                        <button onClick={() => setData({...data, escalations: data.escalations.filter((_, idx) => idx !== i)})} className="absolute top-4 right-4 text-ink-secondary hover:text-status-danger"><Trash2 size={22}/></button>

                                        {/* Escalation Header Logic */}
                                        <div className="grid grid-cols-2 gap-12 mb-8">
                                            <div className="space-y-6">
                                                <DataField label={`Point #${i+1} Description`} value={esc.description} onChange={v => { const ne = [...data.escalations]; ne[i].description = v; setData({...data, escalations: ne}) }} />
                                                <div className="flex flex-col gap-2">
                                                    <label className="text-[10px] font-black text-ink-secondary uppercase italic">Evaluation Condition (SQL Clause)</label>
                                                    <textarea
                                                        className="w-full h-24 border-2 border-line-subtle p-4 outline-none focus:border-primary font-bold bg-surface-panel text-primary rounded shadow-inner font-mono text-[11px]"
                                                        value={esc.condition}
                                                        placeholder="e.g. status='INPRG' AND priority=1"
                                                        onChange={e => { const ne = [...data.escalations]; ne[i].condition = e.target.value; setData({...data, escalations: ne}) }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-center">
                                                <div className="p-6 bg-primary-dark text-white rounded shadow-2xl">
                                                    <div className="flex items-center gap-2 mb-3 border-b border-white/20 pb-2">
                                                        <AlertTriangle size={18} className="text-yellow-400 animate-pulse"/>
                                                        <span className="text-[10px] uppercase font-black tracking-widest">Escalation Engine Rules</span>
                                                    </div>
                                                    <p className="text-[11px] italic leading-relaxed opacity-70">The background engine evaluates this SQL clause against target records. If TRUE, all defined actions below fire simultaneously.</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Nested Actions Logic */}
                                        <div className="mt-6 border-t pt-6 bg-surface-panel/50 -mx-8 -mb-8 p-8 rounded-b">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-[11px] font-black text-primary uppercase tracking-widest flex items-center gap-2">
                                                    <List size={16} className="text-primary"/> Executable Actions for this Point
                                                </h4>
                                                <button onClick={() => addAction(i)} className="text-[9px] bg-primary-dark text-white px-4 py-1.5 rounded font-black hover:bg-black transition-all shadow-md">+ ADD ACTION</button>
                                            </div>

                                            <table className="w-full text-[11px] bg-surface-card border rounded">
                                                <thead className="bg-surface-panel text-ink-secondary border-b">
                                                    <tr>
                                                        <th className="p-3 text-left w-1/4">Action Type</th>
                                                        <th className="p-3 text-left">Target Value / Subject</th>
                                                        <th className="p-3 text-left w-1/4">Recipient Role</th>
                                                        <th className="p-3 w-12 text-center"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(esc.actions || []).map((act, ai) => (
                                                        <tr key={ai} className="border-b border-dashed last:border-0">
                                                            <td className="p-3">
                                                                <select
                                                                    className="w-full bg-transparent font-black outline-none text-primary uppercase"
                                                                    value={act.action_type}
                                                                    onChange={(e) => {
                                                                        const ne = [...data.escalations];
                                                                        ne[i].actions[ai].action_type = e.target.value;
                                                                        setData({...data, escalations: ne});
                                                                    }}
                                                                >
                                                                    <option value="NOTIFICATION">NOTIFICATION (EMAIL)</option>
                                                                    <option value="STATUS_CHANGE">CHANGE STATUS</option>
                                                                    <option value="SCRIPT">RUN AUTOMATION SCRIPT</option>
                                                                </select>
                                                            </td>
                                                            <td className="p-3">
                                                                <input
                                                                    className="w-full bg-transparent outline-none italic font-black text-ink"
                                                                    placeholder={act.action_type === 'STATUS_CHANGE' ? 'e.g. URGENT' : 'e.g. Critical SLA Breach Notification'}
                                                                    value={act.value}
                                                                    onChange={(e) => {
                                                                        const ne = [...data.escalations];
                                                                        ne[i].actions[ai].value = e.target.value;
                                                                        setData({...data, escalations: ne});
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="p-3">
                                                                <input
                                                                    className="w-full bg-transparent outline-none font-black text-status-warning cursor-pointer"
                                                                    placeholder="Double Click to Select Role"
                                                                    value={act.recipient_role}
                                                                    readOnly
                                                                    onDoubleClick={() => setLookup({isOpen: true, title: 'Lookup Roles', type: 'roles', field: `esc_${i}_act_${ai}`})}
                                                                />
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <button onClick={() => {
                                                                    const ne = [...data.escalations];
                                                                    ne[i].actions = ne[i].actions.filter((_, idx) => idx !== ai);
                                                                    setData({...data, escalations: ne});
                                                                }}><X size={16} className="text-ink-secondary hover:text-status-danger"/></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {(!esc.actions || esc.actions.length === 0) && (
                                                        <tr><td colSpan="4" className="p-6 text-center text-ink-secondary italic">No actions defined for this point. The engine will record the event only.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                                {data.escalations.length === 0 && (
                                    <div className="text-center py-20 opacity-20 italic font-black uppercase tracking-[0.4em]">Node Offline: No Escalation Logic defined</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- STATUS FOOTER --- */}
            <div className="bg-primary-dark text-white p-2 px-10 flex justify-between items-center shadow-inner shrink-0 z-50">
                <div className="flex items-center gap-10 text-[10px] font-black uppercase tracking-widest">
                    <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-status-warning"/><span>Data Node: <span className="text-status-warning">{data.site_id}</span></span></div>
                    <div className="flex items-center gap-2 border-l border-white/10 pl-10"><Zap size={14} className="text-yellow-400"/><span>System Ranking: <span className="text-yellow-300">{data.ranking}</span></span></div>
                </div>
                <div className="flex items-center gap-5 italic text-ink-secondary text-[9px]">
                    <span>User Context: {session.username}</span>
                    <div className="w-2 h-2 bg-status-success rounded-full animate-pulse"></div>
                </div>
            </div>

            {/* MODALS */}
            {showStatusModal && <StatusModal currentStatus={data.status} recordId={data.slanum} appName="SLA" onClose={() => { setShowStatusModal(false); loadSLA(); }} />}
            {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="SLA" recordId={data.slanum} onClose={() => setShowHistoryModal(false)} />}
            <AttachmentsModal isOpen={isDocModalOpen} ownertable="SLA" ownerid={data.slanum} onClose={() => {setIsDocModalOpen(false); loadSLA();}} />
            {isWFHistoryOpen && <WorkflowHistoryModal module="SLA" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}
            <AuditHistoryModal
                isOpen={showAudit}
                onClose={() => setShowAudit(false)}
                tableName="sla" // 🎯 من الـ Wizard Domain
                recordId={data.slanum}
            />
        </div>
    );
};

// --- Standard UI Helpers ---

const HeaderField = ({ label, value, onChange, width, isBold, disabled, isStatus, onClick, placeholder, type="text" }) => (
    <div className="flex flex-col border-r px-5 last:border-0 border-line-subtle shrink-0">
        <label className="text-[8px] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
        <div className={`flex items-center gap-2 ${isStatus ? 'cursor-pointer hover:bg-surface-panel p-1 rounded-sm' : ''}`} onClick={onClick}>
            {isStatus && <span className="w-2.5 h-2.5 rounded-full bg-status-success animate-pulse shadow-md"></span>}
            <input
                type={type}
                className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary text-[13px]' : 'text-primary text-[12px]'} bg-transparent transition-all focus:border-primary placeholder:opacity-30`}
                value={value || ''}
                disabled={disabled}
                onChange={e => onChange && onChange(e.target.value)}
                placeholder={placeholder}
            />
        </div>
    </div>
);

const SectionCard = ({ title, children }) => (
    <div className="bg-surface-card border-2 border-line-subtle rounded shadow-xl overflow-hidden h-full">
        <div className="p-3 bg-surface-panel border-b-2 border-line-subtle font-black text-ink-secondary uppercase text-[10px] tracking-widest flex items-center gap-3 italic">
            <div className="w-1.5 h-5 bg-status-warning rounded-full"></div> {title}
        </div>
        <div className="p-8">{children}</div>
    </div>
);

const DataField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
    <div className="flex flex-col gap-1 border-b-2 border-line-subtle group focus-within:border-primary-dark transition-all py-1.5 px-1">
        <label className="text-ink-secondary font-black text-[9px] uppercase tracking-widest italic leading-none mb-1 group-hover:text-primary transition-colors">{label}</label>
        <div className="flex items-center justify-between pb-0.5 h-6">
            <input
                type={type}
                className="outline-none font-black text-primary bg-transparent w-full text-[13px] uppercase disabled:opacity-50"
                value={value ? (type === 'date' ? value.split('T')[0] : value) : ''}
                disabled={disabled}
                onChange={e => onChange && onChange(e.target.value)}
            />
            {onSearch && <Search size={16} className="text-primary cursor-pointer hover:text-primary-dark transition-all hover:scale-125" onClick={onSearch} />}
        </div>
    </div>
);

const StaticField = ({ label, value, icon }) => (
    <div className="flex flex-col border-b border-line-subtle py-2.5 px-3 bg-white/40 shadow-inner rounded-sm mb-2 border-l-4 border-primary/10">
        <div className="flex items-center gap-2 mb-1.5">
            {icon && <span className="text-primary">{icon}</span>}
            <label className="text-[9px] font-black text-ink-secondary uppercase tracking-widest italic">{label}</label>
        </div>
        <div className="font-black uppercase text-[13px] text-primary italic leading-none">{value || 'NOT_DEFINED'}</div>
    </div>
);

export default SLADetails;
